import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { EmailService } from '../../../services/EmailService';
import { Event } from '../../../services/EventService';
import { supabase } from '../../../config/supabase';
import { AuthModal } from '../../auth/components/AuthModal';
import { slugify } from '../../../utils/slugify';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, CheckCircle, Info, AlertCircle, Calendar, Clock, MapPin, Mail, Users, Check, Crown } from 'lucide-react';

interface TokenValidation {
  isValid: boolean;
  email?: string;
  role?: string;
  error?: any;
}

const AcceptCoHostInvitationPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [tokenValidation, setTokenValidation] = useState<TokenValidation | null>(null);
  const [eventData, setEventData] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const token = searchParams.get('token');

  const initInvitationFlow = async () => {
    if (!token || !eventId) {
      setMessage({ type: 'error', text: 'Invalid invitation link. Missing parameters.' });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const validation = await EmailService.verifyManagerToken(token, eventId);
      setTokenValidation(validation);

      if (!validation.isValid || validation.role !== 'cohost') {
        setMessage({ type: 'error', text: 'This invitation link is invalid, expired, or not for a co-host role.' });
        setLoading(false);
        return;
      }

      const { data: event, error: eventError } = await Event.getById(eventId);
      if (eventError || !event) {
        setMessage({ type: 'error', text: 'The event associated with this invitation could not be found.' });
        setLoading(false);
        return;
      }
      setEventData(event);

      if (user) {
        if (user.email !== validation.email) {
            setMessage({ type: 'info', text: `This invitation is for ${validation.email}. Please log in with the correct account.` });
            setShowAuthModal(true);
            return;
        }
        
        setShowAuthModal(false);

        const { error } = await Event.acceptCoHostInvitation(
          eventId,
          user.email!,
          { id: user.id, name: user.user_metadata?.full_name, avatar_url: user.user_metadata?.avatar_url }
        );

        if (error && !error.message?.includes('Already confirmed')) {
          setMessage({ type: 'error', text: 'Failed to accept invitation: ' + error.message });
          return;
        }
        
        setMessage({
          type: 'success',
          text: `You are now a co-host for "${event.title}"! Redirecting...`
        });

        await EmailService.markTokenAsUsed(token);

        setTimeout(() => navigate(`/events/${eventId}/dashboard`), 3000);
      } else {
        setShowAuthModal(true);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'An unexpected error occurred while processing your invitation.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initInvitationFlow();
  }, [eventId, token, user]);

  const handleAuthSuccess = () => {
    // The useEffect will re-trigger when the user state is updated.
    setShowAuthModal(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600 font-medium">Validating your co-host invitation...</p>
        </div>
      </div>
    );
  }

  if (message?.type === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg p-4">
        <Card className="max-w-md w-full text-center border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-3">Success!</h2>
            <p className="text-green-700 mb-6 text-base">{message.text}</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Redirecting to your event dashboard...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!tokenValidation?.isValid || !eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg p-4">
        <Card className="max-w-md w-full text-center border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-800 mb-3">Invitation Error</h2>
            <p className="text-red-700 mb-6">
              {message?.text || 'This invitation link is invalid or has expired.'}
            </p>
            <div className="space-y-2">
              <Button onClick={() => navigate('/')} className="w-full">
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen brand-soft-bg">
      <div className="max-w-2xl mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Co-Host Invitation</h1>
          <p className="text-xl text-gray-600">
            You've been invited to help manage this event
          </p>
        </div>

        {/* Event Card */}
        <Card className="mb-8 border-0 shadow-lg overflow-hidden">
          {eventData.image_url && (
            <div className="h-48 bg-gradient-to-r from-blue-500 to-purple-600 relative">
              <img 
                src={eventData.image_url} 
                alt={eventData.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20" />
            </div>
          )}
          
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900">{eventData.title}</CardTitle>
            {eventData.short_description && (
              <CardDescription className="text-base text-gray-600 mt-2">
                {eventData.short_description}
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 text-gray-600">
                <Calendar className="w-5 h-5 text-blue-500" />
                <span className="font-medium">
                  {new Date(eventData.start_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              
              <div className="flex items-center space-x-3 text-gray-600">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="font-medium">{eventData.start_time}</span>
              </div>
              
              {eventData.location && (
                <div className="flex items-center space-x-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">{eventData.location}</span>
                </div>
              )}
              
              <div className="flex items-center space-x-3 text-gray-600">
                <Mail className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Invited as: {tokenValidation.email}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 pt-2">
              <Crown className="w-5 h-5 text-purple-500" />
              <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200">
                Co-Host Role
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Co-host Privileges */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-blue-900">As a co-host, you can:</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-blue-800">
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>View the attendee list and details</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Mark attendance during the event</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Help with general event coordination</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Support the main host with event management</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Info Messages */}
        {message && message.type === 'info' && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Info className="w-5 h-5 text-blue-500 mr-2" />
                <p className="text-blue-700">{message.text}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Messages */}
        {message && message.type === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700">{message.text}</p>
            </div>
          </div>
        )}

        {/* Auth Modal */}
        <AuthModal
          skipRedirect={true}
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultEmail={tokenValidation?.email}
          onSuccess={handleAuthSuccess}
          title="Accept Your Co-Host Invitation"
          subtitle={`Sign in or create an account with ${tokenValidation?.email} to continue.`}
          redirectGoogleUri={`${window.location.origin}/events/${eventId}/cohost?token=${token}`}
        />
      </div>
    </div>
  );
};

export default AcceptCoHostInvitationPage;